import PropTypes from 'prop-types';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Icon } from '@iconify/react';
import bellFill from '@iconify/icons-eva/bell-fill';
import clockFill from '@iconify/icons-eva/clock-fill';
import doneAllFill from '@iconify/icons-eva/done-all-fill';
import InfiniteScroll from 'react-infinite-scroller';
// material
import { alpha } from '@mui/material/styles';
import {
  Box,
  List,
  Badge,
  Tooltip,
  Divider,
  ListItem,
  IconButton,
  Typography,
  ListItemText,
  CircularProgress
} from '@mui/material';
// utils
import { useDispatch, useSelector } from 'react-redux';

// components
import Scrollbar from '../../components/Scrollbar';
import MenuPopover from '../../components/MenuPopover';
import { getAllNotification, updateNotification } from '../../service/notification.service';
import { ADD_NOTIFICATIONS, MARK_ALL_READ, UPDATE_NOTIFICATION } from '../../redux/constants';

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = (
    <Typography variant="subtitle2">
      <Typography variant="subtitle2">{notification.title}</Typography>
      <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
        {notification.message}
      </Typography>
    </Typography>
  );

  return {
    title
  };
}

NotificationItem.propTypes = {
  notification: PropTypes.object.isRequired,
  handleClose: PropTypes.func
};

function NotificationItem({ notification, handleClose }) {
  const { title } = renderContent(notification);
  const dispatch = useDispatch();

  const handleRead = async () => {
    handleClose();
    const res = await updateNotification(notification.id, { isRead: true });
    if (res) {
      dispatch({
        type: UPDATE_NOTIFICATION,
        payload: {
          data: res
        }
      });
    }
  };

  return (
    <ListItem
      button
      to={`/dashboard/classes/${notification.classId}`}
      disableGutters
      component={RouterLink}
      onClick={handleRead}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(!notification.isRead && {
          bgcolor: 'action.selected'
        })
      }}
    >
      {/* <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
      </ListItemAvatar> */}
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled'
            }}
          >
            <Box component={Icon} icon={clockFill} sx={{ mr: 0.5, width: 16, height: 16 }} />
            {formatDistanceToNow(new Date(notification.createdAt))}
          </Typography>
        }
      />
    </ListItem>
  );
}

export default function NotificationsPopover() {
  const anchorRef = useRef(null);
  // const currentUser = useSelector((state) => state.auth.currentUser);
  const selectedOrg = useSelector((state) => state.org.selectedOrg);
  const orgLoading = useSelector((state) => state.org.orgLoading);
  const notifications = useSelector((state) => state.notification.data);
  const notificationOffset = useSelector((state) => state.notification.offset);
  const notificationHasMore = useSelector((state) => state.notification.hasMore);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [isDataFetching, setIsDataFetching] = useState(false);

  useEffect(() => {
    const fetchNotifications = async () => {
      const notiRes = await getAllNotification(selectedOrg?.id ?? selectedOrg?._id);
      if (notiRes) {
        dispatch({
          type: ADD_NOTIFICATIONS,
          payload: {
            data: notiRes.results,
            offset: notificationOffset + 1
          }
        });
      }
    };
    if (notifications === null && (selectedOrg?.id ?? selectedOrg?.orgId) && !orgLoading)
      fetchNotifications();
  }, []);

  const fetchNextData = async () => {
    if (notificationHasMore) {
      const res = await getAllNotification(selectedOrg?.id ?? selectedOrg?._id, notificationOffset);
      if (res) {
        dispatch({
          type: ADD_NOTIFICATIONS,
          payload: {
            data: res.results,
            offset: notificationOffset + 1
          }
        });
      }
      setIsDataFetching(false);
    }
  };

  const totalUnRead = useMemo(() => {
    if (notifications && notifications.length > 0) {
      return notifications.filter((item) => item.isRead === false).length;
    }
    return 0;
  }, [notifications]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    handleClose();
    const notificationReqArray = notifications
      .filter((item) => item.isRead === false)
      .map((item) => updateNotification(item.id, { isRead: true }));

    const res = await Promise.all(notificationReqArray);

    if (res) {
      dispatch({
        type: MARK_ALL_READ
      });
    }
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        color={open ? 'primary' : 'default'}
        sx={{
          ...(open && {
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.focusOpacity)
          })
        }}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Icon icon={bellFill} width={20} height={20} />
        </Badge>
      </IconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 360 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              You have {totalUnRead} unread messages
            </Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title=" Mark all as read">
              <IconButton color="primary" onClick={handleMarkAllAsRead}>
                <Icon icon={doneAllFill} width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider />

        {notifications && notifications.length > 0 ? (
          <Scrollbar sx={{ height: { xs: 340, sm: 400 }, maxHeight: 'fit-content' }}>
            <Box id="scrollableCard">
              <InfiniteScroll
                pageStart={0}
                loadMore={() => {
                  if (notificationHasMore && !isDataFetching) {
                    fetchNextData();
                    setIsDataFetching(true);
                  }
                }}
                getScrollParent={() => document.getElementById('scrollableCard')}
                hasMore={notificationHasMore}
                loader={
                  <Box my={2} display="flex" justifyContent="center">
                    <CircularProgress />
                  </Box>
                }
                useWindow={false}
              >
                <List disablePadding>
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      handleClose={handleClose}
                    />
                  ))}
                </List>
              </InfiniteScroll>
            </Box>
          </Scrollbar>
        ) : (
          <Typography pt={2} pb={2} textAlign="center">
            No notification
          </Typography>
        )}
      </MenuPopover>
    </>
  );
}
